/**
 * The booking-link editor's form model (#685).
 *
 * Defaults, the open-time mapping from a stored link and the client-side
 * validation, lifted out of BookingLinkModal so the grouped editor keeps
 * sending exactly the payload the flat editor sent. That equivalence is
 * pinned by pages/__tests__/BookingLinks.editor.contract.test.jsx — change a
 * default here and that suite should fail. #790 is the one intended exception:
 * a 1:1 link opened with several coaches saves only the coach it books
 * (`oneOnOneAssignees`), pinned in the same suite.
 */

// The editor's "auto" calendar. Radix's Select rejects an empty SelectItem
// value (it reserves '' for "cleared"), so the picker carries this sentinel
// and the form state carries '' — which is what the backend's
// _validate_booking_calendar treats as "not chosen" (None/'' → None).
export const AUTO_CALENDAR = 'auto';

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const DEFAULT_CONFIRMATION_MESSAGE = 'Your meeting has been scheduled!';

// #790: the same string as ONE_ON_ONE_ONE_COACH in services/booking_team.py.
export const ONE_ON_ONE_ONE_COACH = 'A 1:1 link has one coach. Pick one coach, or switch the type to Round Robin';

const defaultAvailability = () => ({ days: [0, 1, 2, 3, 4], start_hour: 9, start_min: 0, end_hour: 17, end_min: 0 });

// Zoom was retired 2026-09-04 (#513). Links saved with location 'zoom' are
// left in place (fail-closed on the backend) and surface here as Custom so the
// owner sees a valid selection; saving the form heals the stored value.
// Every other stored value — google_meet, teams (#514), phone, in_person,
// custom — passes through unchanged.
export const normalizeLegacyLocation = (loc) => (loc === 'zoom' ? 'custom' : (loc || 'google_meet'));

// D5 (#514): a booking link persists the calendar that HOSTS its events, chosen
// by the owner in the editor. Links saved before the field existed carry none.
//
// The editor must NOT guess one. `_resolve_booking_calendar` (routes/booking.py)
// resolves a link with no `calendar` at BOOKING time — Google when the assignee
// actually has Google connected, else Outlook — and this editor has no
// connection data to consult. Guessing 'google' here and shipping it in the PUT
// (the whole `form` is the payload) permanently pinned a Microsoft-only coach's
// link to a calendar they do not have, after which it created no event ever
// again (#514 audit A02). So "no stored calendar" stays unchosen ('' = auto)
// and is saved back as null.
//
// The one safe inference survives: only Outlook can host a Teams meeting, so a
// legacy link whose location is 'teams' IS an Outlook link.
export const inferLinkCalendar = (link) => {
    if (link?.calendar) return link.calendar;
    if (link?.location === 'teams') return 'outlook';
    return '';
};

/** A new link's form. `ownerZone` is the #579 default — see BookingLinkModal. */
export function newBookingLinkForm(ownerZone) {
    return {
        name: '', slug: '', description: '', duration: 30, buffer_time: 0,
        booking_type: 'one_on_one', calendar: '', location: 'google_meet', color: '#1a1a1a',
        max_days_ahead: 60, min_notice_hours: 1, confirmation_message: DEFAULT_CONFIRMATION_MESSAGE,
        timezone: ownerZone,
        availability: defaultAvailability(),
        assigned_users: [], custom_questions: [],
        // #758: new links offer the guest coach picker by default. It is only
        // sent as true for round robin (BookingLinkModal's payload).
        allow_guest_coach_choice: true,
    };
}

/** The form for an existing link — every fallback is the pre-#685 one. */
export function bookingLinkFormFromExisting(existing, ownerZone) {
    return {
        name: existing.name || '',
        slug: existing.slug || '',
        description: existing.description || '',
        duration: existing.duration || 30,
        buffer_time: existing.buffer_time || 0,
        booking_type: existing.booking_type || 'one_on_one',
        calendar: inferLinkCalendar(existing),
        location: normalizeLegacyLocation(existing.location),
        color: existing.color || '#1a1a1a',
        max_days_ahead: existing.max_days_ahead || 60,
        min_notice_hours: existing.min_notice_hours || 1,
        confirmation_message: existing.confirmation_message || '',
        timezone: existing.timezone || ownerZone,
        availability: existing.availability || defaultAvailability(),
        assigned_users: existing.assigned_users || [],
        custom_questions: existing.custom_questions || [],
        // #758: an existing link without the field (every link saved before
        // it existed) stays off, so live public pages do not change.
        allow_guest_coach_choice: existing.allow_guest_coach_choice === true,
    };
}

/**
 * The window's wall-clock times as the time inputs display them. `??`, not
 * `||`: an hour of 0 is midnight, exactly as routes/booking.py
 * (`avail.get("end_hour", 17)`) and PublicBooking.jsx read it — the flat
 * editor's `||` showed a midnight boundary as 09:00/17:00 while the server
 * used 00:00. Validation reads the same values, so it matches the screen.
 */
export function availabilityTimes(availability = {}) {
    return {
        startHour: availability.start_hour ?? 9,
        startMin: availability.start_min ?? 0,
        endHour: availability.end_hour ?? 17,
        endMin: availability.end_min ?? 0,
    };
}

const pad2 = (n) => String(n).padStart(2, '0');

export const formatClock = (hour, min) => `${pad2(hour)}:${pad2(min)}`;

/** "Mon–Fri" · "Mon, Wed" · "Mon–Wed, Sat" — runs of three or more collapse. */
export function formatDays(days = []) {
    const sorted = [...new Set(days)].filter((d) => DAYS[d]).sort((a, b) => a - b);
    if (sorted.length === 0) return 'No days selected';
    const runs = [];
    sorted.forEach((d) => {
        const last = runs[runs.length - 1];
        if (last && d === last[last.length - 1] + 1) last.push(d);
        else runs.push([d]);
    });
    return runs
        .map((run) => (run.length >= 3
            ? `${DAYS[run[0]]}–${DAYS[run[run.length - 1]]}`
            : run.map((d) => DAYS[d]).join(', ')))
        .join(', ');
}

/**
 * The four editor steps, in order. `value` is the tab id; every validation
 * error names the step that holds its field so the editor can open it.
 */
export const EDITOR_STEPS = [
    { value: 'details', label: 'Details' },
    { value: 'schedule', label: 'Date and Time' },
    { value: 'integration', label: 'Integration' },
    { value: 'others', label: 'Others' },
];

/**
 * Client-side validation. Returns errors in step order; each carries the DOM
 * id of the field to focus, the step that holds it and whether it sits inside
 * the collapsed Advanced group.
 *
 * `name` is the flat editor's one rule. #685 D2 adds the cases that used to
 * save silently and then offer a booker nothing (no day, an empty window) or
 * nonsense (a negative booking window or notice); #753 adds "no coach".
 *
 * `members` (optional) are the assignment rows from `buildMemberOptions`,
 * passed only once the coach list has loaded. With them, two more #753 rules
 * (stack contract A8.3) apply on the Details step: a link whose only
 * assignees are removed users has nobody to book, and a round-robin link
 * needs at least one assigned active coach with a weight above 0. Without
 * them (loading / failed) only the plain "no assignees" rule runs, so a
 * failed coach fetch never blocks a save on a guess.
 *
 * #790 adds the 1:1 rule: a link that is not round robin holds one coach
 * (the server refuses more). It needs no member data and can only fire while
 * the coach list is loading or failed — once it loads, the editor has already
 * kept the one coach the link books (`oneOnOneAssignees`).
 */
export function validateBookingLinkForm(form, members) {
    const errors = [];
    if (!form.name) {
        errors.push({ field: 'bl-name', step: 'details', message: 'Name is required' });
    }
    // #753: a link with nobody assigned offers no slots and refuses every
    // booking (the server fails closed), so it cannot be saved — on create or
    // edit. The server enforces the same rule.
    const assigned = form.assigned_users || [];
    const rowById = new Map((members || []).map(r => [r.user_id, r]));
    const onlyRemoved = !!members && assigned.every(a => isRemovedRow(rowById.get(a.user_id)));
    if (!assigned.length || onlyRemoved) {
        errors.push({ field: 'bl-assigned-users', step: 'details', message: 'Pick at least one coach' });
    } else if (members && form.booking_type === 'round_robin'
        && !assigned.some(a => rowById.get(a.user_id)?.addable && Number(a.weight) > 0)) {
        errors.push({
            field: 'bl-assigned-users', step: 'details',
            message: 'Round robin needs at least one active coach with a weight above 0',
        });
    } else if (form.booking_type !== 'round_robin' && assigned.length > 1) {
        errors.push({ field: 'bl-assigned-users', step: 'details', message: ONE_ON_ONE_ONE_COACH });
    }
    if (!(form.availability?.days || []).length) {
        errors.push({ field: 'bl-day-0', step: 'schedule', message: 'Pick at least one available day' });
    }
    const t = availabilityTimes(form.availability);
    if (t.endHour * 60 + t.endMin <= t.startHour * 60 + t.startMin) {
        errors.push({ field: 'bl-end-time', step: 'schedule', message: 'End time must be after the start time' });
    }
    if (!(Number(form.max_days_ahead) >= 1)) {
        errors.push({ field: 'bl-max-days', step: 'schedule', advanced: true, message: 'Max days ahead must be at least 1' });
    }
    if (!(Number(form.min_notice_hours) >= 0)) {
        errors.push({ field: 'bl-min-notice', step: 'schedule', advanced: true, message: 'Minimum notice cannot be negative' });
    }
    return errors;
}

/**
 * #753: rows of the assignment list. Coaches first, in coach order (GET
 * /admin/coaches sorts by order, then name); an inactive (soft-deleted) coach
 * only while assigned. Assigned user_ids with no coach profile follow:
 * 'not_coach' (grandfathered — still booked until unticked) or, when the user
 * is no longer in the org, 'removed' (the server drops it on save). When
 * /admin/users could not be loaded (`usersLoaded` false) nobody can be told
 * apart from a removed user, so such rows stay 'not_coach' ("Unknown user").
 *
 * `status` is 'active' | 'paused' | 'suspended' | 'inactive' | 'removed' for
 * coaches and 'active' otherwise; only an active coach can be ADDED, anything
 * can be removed. A coach profile can outlive its user's membership: with
 * /admin/users loaded, a coach whose user is not in it has left the org and
 * is 'removed' (#790 audit) — the server refuses to newly assign them
 * (NOT_IN_ORG), drops them on save and never books them — so, like any
 * departed assignee, they are listed only while assigned. 'suspended' (stack contract A8.1) is a coach whose user account is
 * suspended (`/admin/users` row `is_active: false`); the server refuses to
 * newly assign one.
 *
 * #790 adds two flags. `addable`: only an active coach can be newly assigned
 * (the checklist, the 1:1 dropdown and the round-robin rule all read it).
 * `eligible`: would the server book this person on a 1:1 link —
 * booking_team's resolve_link_members drops users who left the org and
 * eligible_members drops paused, inactive and suspended people (weight is
 * ignored on 1:1). Unknown users (`usersLoaded` false) count as eligible.
 */
export function buildMemberOptions(coaches = [], users = [], assigned = [], usersLoaded = true) {
    const userById = new Map(users.map(u => [u.user_id, u]));
    const assignedIds = new Set(assigned.map(a => a.user_id));
    const leftOrg = (c) => usersLoaded && !userById.has(c.user_id);
    const rows = coaches
        .filter(c => c.user_id && ((c.active !== false && !leftOrg(c)) || assignedIds.has(c.user_id)))
        .map(c => {
            const status = leftOrg(c) ? 'removed'
                : c.active === false ? 'inactive'
                    : (userById.get(c.user_id)?.is_active === false ? 'suspended' : (c.paused ? 'paused' : 'active'));
            return {
                user_id: c.user_id,
                kind: 'coach',
                status,
                addable: status === 'active',
                eligible: status === 'active',
                display_name: c.display_name || userById.get(c.user_id)?.name || 'Coach',
                title: c.title || '',
                photo_url: c.photo_url || '',
                calendar_connected: !!userById.get(c.user_id)?.calendar_connected,
            };
        });
    const listed = new Set(rows.map(r => r.user_id));
    assigned.forEach(({ user_id }) => {
        if (listed.has(user_id)) return;
        listed.add(user_id);
        const u = userById.get(user_id);
        rows.push({
            user_id,
            kind: u || !usersLoaded ? 'not_coach' : 'removed',
            status: 'active',
            addable: false,
            eligible: u ? u.is_active !== false : !usersLoaded,
            display_name: u ? (u.name || u.email || 'Team member') : (usersLoaded ? 'Removed user' : 'Unknown user'),
            title: '',
            photo_url: '',
            calendar_connected: !!u?.calendar_connected,
        });
    });
    return rows;
}

/** A row for someone no longer in the org: a removed user, or a coach whose user left. */
export const isRemovedRow = (row) => row?.kind === 'removed' || row?.status === 'removed';

/**
 * #790: the one assignee a 1:1 link keeps — the coach its public page books
 * today. `link_hosts` (services/booking_team.py) takes the first ELIGIBLE
 * member in stored order, so this does too; with nobody eligible it keeps the
 * first entry, which the editor shows with its flag so it can be replaced.
 * The kept entry is returned as stored, weight included.
 */
export function oneOnOneAssignees(assigned = [], members = []) {
    if (!assigned.length) return [];
    const rowById = new Map(members.map(r => [r.user_id, r]));
    return [assigned.find(a => rowById.get(a.user_id)?.eligible) || assigned[0]];
}
